import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import React from "react";
import { GridListItem } from "react-aria-components";

import { ActiveHoldIndicator, StatIndicator } from "@/components/Indicators";
import { RoleCondition } from "@/components/RoleCondition";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import {
  AccessionDialogProvider,
  useSetDialogState,
} from "@/features/accession/components/detail/state";
import { ReceiveDialog } from "@/features/accession/components/dialogs/ReceiveDialog";
import {
  TestPreviewHoverCard,
  TestPreviewProvider,
} from "@/features/accession/components/TestOrderPreview";
import { useView } from "@/features/view/components/ViewProvider";
import type { TAccession } from "@/lib/types";
import { format } from "@/lib/utils";
import { useNavLocationState } from "@/utils/useNavStack";

export function LoadingItem() {
  return (
    <div className="p-3 h-[100px] rounded-md border bg-background shadow-xs">
      <div className="flex gap-3">
        <div className="flex flex-col justify-between grow gap-3">
          <Skeleton style={{ width: 70 }} />
          <Skeleton style={{ width: 100 }} />
          <Skeleton style={{ width: 120 }} />
        </div>
        <Skeleton style={{ width: 50 }} />
      </div>
    </div>
  );
}

function AccessionBoardItem({ accession }: { accession: TAccession }) {
  const navigate = useNavigate();
  const { href, label } = useNavLocationState();
  const { settings } = useView();

  return (
    <GridListItem
      className="block p-3 h-[100px] outline-0 rounded-md bg-background focus-visible:outline-2 focus-visible:outline-ring/70 focus-visible:-outline-offset-2 shadow-xs text-left cursor-pointer"
      aria-label={`Accession ${accession.id}`}
      href={{
        to: "/accessions/$id",
        params: { id: accession.id },
        state: { href, selected: accession.id, label },
      }}
    >
      <TestPreviewProvider>
        <AccessionDialogProvider>
          <div className="flex gap-3 h-full">
            <div className="flex flex-col justify-between flex-1 min-w-0">
              <div className="flex items-center gap-2 pb-3">
                <Text mono size="sm" muted>
                  {accession.id}
                </Text>
                <TestPreviewHoverCard accession={accession} />
              </div>
              <div className="flex items-center gap-2">
                <Text size="sm" truncate>
                  {accession.caseId}
                </Text>
                {accession.hasHold && <ActiveHoldIndicator />}
                {accession.priority === "STAT" && <StatIndicator />}
              </div>
              <Text size="1" muted truncate>
                {accession.org.name}
              </Text>
            </div>
            <div className="flex flex-col justify-between align-end">
              <Text size="1" muted className="text-right">
                {settings.dateRelative === "relative"
                  ? dayjs(accession.date).fromNow(true)
                  : format(accession.date)}
              </Text>

              {accession.status === "new" ? (
                <RoleCondition
                  roles={[
                    "blis_lab_manager",
                    "blis_lab_tech",
                    "blis_lab_assistant",
                  ]}
                >
                  <div className="text-right">
                    <ReceiveButton />
                  </div>
                </RoleCondition>
              ) : accession.status === "completed" ? (
                <RoleCondition roles={["blis_lab_manager", "blis_lab_tech"]}>
                  <Button
                    aria-label="Generate report"
                    size="sm"
                    variant="outline"
                    onPress={(e) => {
                      e.continuePropagation();
                      navigate({
                        to: "/accessions/$id/report",
                        params: { id: accession.id },
                        state: { href, selected: accession.id, label },
                      });
                    }}
                  >
                    Generate report
                  </Button>
                </RoleCondition>
              ) : null}
            </div>
          </div>
          <ReceiveDialog accession={accession} />
        </AccessionDialogProvider>
      </TestPreviewProvider>
    </GridListItem>
  );
}

function ReceiveButton() {
  const setShowReceive = useSetDialogState("receive");

  return (
    <Button
      aria-label="Receive accession"
      size="sm"
      variant="outline"
      type="button"
      onPress={() => {
        setShowReceive(true);
      }}
    >
      Receive
    </Button>
  );
}

const _AccessionBoardItem = React.memo(AccessionBoardItem);

export { _AccessionBoardItem as AccessionBoardItem };
